import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { Modal, Badge, Button, Spinner } from 'react-bootstrap';
import { getArticleImageSlides } from '../utils/imageHelper';
import ArticleImageModal from './ArticleImageModal';

/**
 * Componente en miniatura con fallback automático entre las URLs candidatas de cada imagen.
 */
const CandidateThumbnail = ({ slide, alt, className, style, onClick }) => {
  const [candIdx, setCandIdx] = useState(0);
  const candidates = slide?.candidates || [];
  const src = candidates[candIdx] || '';

  useEffect(() => {
    setCandIdx(0);
  }, [slide?.raw]);

  if (!src) {
    return (
      <div
        className={`d-flex align-items-center justify-content-center bg-light text-muted border rounded ${className || ''}`}
        style={style}
        onClick={onClick}
      >
        <i className="bi bi-image"></i>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt || slide?.raw || 'Imagen'}
      className={className}
      style={style}
      onClick={onClick}
      onError={() => {
        if (candIdx < candidates.length - 1) {
          setCandIdx((prev) => prev + 1);
        }
      }}
    />
  );
};

const ArticleImagesAdmin = ({ user }) => {
  const [articles, setArticles] = useState([]);
  const [rubros, setRubros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(25);

  // Estadísticas de cobertura de imágenes en la empresa activa
  const [stats, setStats] = useState({
    total_articulos: 0,
    con_imagen: 0,
    sin_imagen: 0,
    db_empresa: '',
    carpeta: '',
  });

  // Filtros de búsqueda
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [searchMode, setSearchMode] = useState('general');
  const [selectedRubro, setSelectedRubro] = useState('');
  const [imageFilter, setImageFilter] = useState('all'); // 'all' | 'with' | 'without'

  // Modal de administración de imágenes de un artículo
  const [showManageModal, setShowManageModal] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMode, setUploadMode] = useState('append'); // 'append' | 'replace'
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  // Modal de vista previa en pantalla completa (carrusel existente)
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewArticle, setPreviewArticle] = useState(null);

  const activeCompanyId = user?.empresa_activa?.c_nuges || user?.empresa_activa?.id || window.__ACTIVE_COMPANY_ID__ || null;
  const activeCompanyName = user?.empresa_activa?.d_nuges || user?.empresa_activa?.nombre || `Empresa ${activeCompanyId || ''}`;

  // Debounce del texto de búsqueda
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 350);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Cargar estadísticas de imágenes de la empresa
  const fetchStats = useCallback(async () => {
    try {
      const headers = activeCompanyId ? { 'X-Company-ID': String(activeCompanyId) } : {};
      const params = activeCompanyId ? { company: String(activeCompanyId) } : {};
      const res = await axios.get('/api/articulos/gestionar-imagenes/', { headers, params });
      if (res.data && res.data.ok) {
        setStats({
          total_articulos: res.data.total_articulos || 0,
          con_imagen: res.data.con_imagen || 0,
          sin_imagen: res.data.sin_imagen || 0,
          db_empresa: res.data.db_empresa || '',
          carpeta: res.data.carpeta || '',
        });
      }
    } catch (err) {
      console.warn('Error obteniendo estadísticas de imágenes:', err);
    }
  }, [activeCompanyId]);

  // Cargar rubros para el filtro
  useEffect(() => {
    const fetchRubros = async () => {
      try {
        const headers = activeCompanyId ? { 'X-Company-ID': String(activeCompanyId) } : {};
        const params = {
          page_size: 500,
          ...(activeCompanyId ? { company: String(activeCompanyId) } : {}),
        };
        const res = await axios.get('/api/rubros/', { headers, params });
        const list = Array.isArray(res.data) ? res.data : (res.data?.results || []);
        setRubros(list);
      } catch (err) {
        console.warn('Error cargando rubros:', err);
      }
    };
    fetchRubros();
    fetchStats();
  }, [activeCompanyId, fetchStats]);

  // Cargar listado de artículos
  const fetchArticles = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const headers = activeCompanyId ? { 'X-Company-ID': String(activeCompanyId) } : {};
      const params = {
        page,
        page_size: pageSize,
        ...(activeCompanyId ? { company: activeCompanyId } : {}),
      };
      if (debouncedSearch.trim()) {
        params.search = debouncedSearch.trim();
        params.search_mode = searchMode;
      }
      if (selectedRubro) {
        params.rubro = selectedRubro;
      }
      if (imageFilter === 'with') {
        params.has_images = 'true';
      } else if (imageFilter === 'without') {
        params.has_images = 'false';
      }

      const res = await axios.get('/api/articulos/', { params, headers });
      if (res.data) {
        if (Array.isArray(res.data)) {
          setArticles(res.data);
          setTotalCount(res.data.length);
        } else {
          setArticles(res.data.results || []);
          setTotalCount(res.data.count || 0);
        }
      } else {
        setArticles([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.warn('Error cargando artículos:', err);
      setArticles([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [activeCompanyId, pageSize, debouncedSearch, searchMode, selectedRubro, imageFilter]);

  useEffect(() => {
    fetchArticles(currentPage);
  }, [fetchArticles, currentPage]);

  const handleOpenManageModal = (article) => {
    setSelectedArticle(article);
    setUploadMode('append');
    setShowManageModal(true);
  };

  const handleOpenPreviewModal = (article) => {
    setPreviewArticle({
      ...article,
      id: article.c_artic,
      name: article.d_artic,
      price: article.i_conta || article.i_lista || 0,
    });
    setShowPreviewModal(true);
  };

  // Sincronizar cambios de un artículo en el estado local de la tabla y del modal
  const updateLocalArticleImages = (cArticTrimmed, newDImagen, newDPathimg, newDImagenwb, fUpdat = null, hUpdat = null) => {
    setArticles((prev) =>
      prev.map((art) => {
        if (String(art.c_artic || '').trim() === String(cArticTrimmed).trim()) {
          return {
            ...art,
            d_imagen: newDImagen,
            d_pathimg: newDPathimg,
            d_imagenwb: newDImagenwb,
            ...(fUpdat ? { f_updat: fUpdat } : {}),
            ...(hUpdat ? { h_updat: hUpdat } : {}),
          };
        }
        return art;
      })
    );
    setSelectedArticle((prev) => {
      if (!prev || String(prev.c_artic || '').trim() !== String(cArticTrimmed).trim()) return prev;
      return {
        ...prev,
        d_imagen: newDImagen,
        d_pathimg: newDPathimg,
        d_imagenwb: newDImagenwb,
        ...(fUpdat ? { f_updat: fUpdat } : {}),
        ...(hUpdat ? { h_updat: hUpdat } : {}),
      };
    });
    fetchStats();
  };

  // Subir uno o varios archivos seleccionados para el artículo activo
  const handleUploadFiles = async (fileList) => {
    if (!selectedArticle || !fileList || fileList.length === 0) return;

    const formData = new FormData();
    if (activeCompanyId) {
      formData.append('company', String(activeCompanyId));
    }
    formData.append('c_artic', String(selectedArticle.c_artic).trim());
    formData.append('mode', uploadMode);

    Array.from(fileList).forEach((file) => {
      formData.append('archivos', file);
    });

    setUploading(true);
    try {
      const headers = activeCompanyId ? { 'X-Company-ID': String(activeCompanyId) } : {};
      const res = await axios.post('/api/articulos/subir-imagen/', formData, { headers });
      if (res.data && res.data.ok) {
        updateLocalArticleImages(
          selectedArticle.c_artic,
          res.data.d_imagen,
          res.data.d_pathimg,
          res.data.d_imagenwb,
          res.data.f_updat,
          res.data.h_updat
        );
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'success',
          title: `Se ${uploadMode === 'append' ? 'agregaron' : 'reemplazaron'} ${res.data.filenames?.length || 1} imagen(es)`,
          showConfirmButton: false,
          timer: 2200,
        });
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'No se pudieron subir las imágenes.';
      Swal.fire({
        icon: 'error',
        title: 'Error al subir',
        text: msg,
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Eliminar una imagen puntual de un artículo
  const handleDeleteSingleImage = async (filename) => {
    if (!selectedArticle || !filename) return;

    const confirm = await Swal.fire({
      title: '¿Eliminar esta imagen?',
      text: `Se desvinculará y eliminará "${filename}" del artículo ${String(selectedArticle.c_artic).trim()}.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
    });

    if (!confirm.isConfirmed) return;

    setUploading(true);
    try {
      const headers = activeCompanyId ? { 'X-Company-ID': String(activeCompanyId) } : {};
      const res = await axios.post(
        '/api/articulos/gestionar-imagenes/',
        {
          company: activeCompanyId ? String(activeCompanyId) : undefined,
          c_artic: String(selectedArticle.c_artic).trim(),
          action: 'delete',
          filename,
          delete_file: true,
        },
        { headers }
      );
      if (res.data && res.data.ok) {
        updateLocalArticleImages(
          selectedArticle.c_artic,
          res.data.d_imagen,
          res.data.d_pathimg,
          res.data.d_imagenwb,
          res.data.f_updat,
          res.data.h_updat
        );
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'success',
          title: 'Imagen eliminada',
          showConfirmButton: false,
          timer: 1800,
        });
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.response?.data?.error || 'No se pudo eliminar la imagen.',
      });
    } finally {
      setUploading(false);
    }
  };

  // Vaciar todas las imágenes de un artículo
  const handleClearAllImages = async () => {
    if (!selectedArticle) return;

    const confirm = await Swal.fire({
      title: '¿Eliminar TODAS las imágenes de este artículo?',
      text: `El artículo ${String(selectedArticle.c_artic).trim()} - ${selectedArticle.d_artic} quedará sin fotografías.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, vaciar todas',
      cancelButtonText: 'Cancelar',
    });

    if (!confirm.isConfirmed) return;

    setUploading(true);
    try {
      const headers = activeCompanyId ? { 'X-Company-ID': String(activeCompanyId) } : {};
      const res = await axios.post(
        '/api/articulos/gestionar-imagenes/',
        {
          company: activeCompanyId ? String(activeCompanyId) : undefined,
          c_artic: String(selectedArticle.c_artic).trim(),
          action: 'clear',
          delete_file: true,
        },
        { headers }
      );
      if (res.data && res.data.ok) {
        updateLocalArticleImages(
          selectedArticle.c_artic,
          '',
          '',
          '',
          res.data.f_updat,
          res.data.h_updat
        );
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'success',
          title: 'Se eliminaron todas las imágenes del artículo',
          showConfirmButton: false,
          timer: 2000,
        });
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.response?.data?.error || 'No se pudieron eliminar las imágenes.',
      });
    } finally {
      setUploading(false);
    }
  };

  // Marcar una imagen como principal (primera en el carrusel / portada)
  const handleSetPrimaryImage = async (filename) => {
    if (!selectedArticle || !filename) return;
    setUploading(true);
    try {
      const headers = activeCompanyId ? { 'X-Company-ID': String(activeCompanyId) } : {};
      const res = await axios.post(
        '/api/articulos/gestionar-imagenes/',
        {
          company: activeCompanyId ? String(activeCompanyId) : undefined,
          c_artic: String(selectedArticle.c_artic).trim(),
          action: 'set_primary',
          filename,
        },
        { headers }
      );
      if (res.data && res.data.ok) {
        updateLocalArticleImages(
          selectedArticle.c_artic,
          res.data.d_imagen,
          res.data.d_pathimg,
          res.data.d_imagenwb,
          res.data.f_updat,
          res.data.h_updat
        );
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'success',
          title: `"${filename}" ahora es la imagen de portada`,
          showConfirmButton: false,
          timer: 1800,
        });
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.response?.data?.error || 'No se pudo cambiar la imagen principal.',
      });
    } finally {
      setUploading(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const selectedSlides = selectedArticle ? getArticleImageSlides(selectedArticle) : [];

  return (
    <div className="container-fluid py-4 px-4">
      {/* Encabezado del Módulo */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3 bg-white p-4 rounded-3 shadow-sm border">
        <div>
          <div className="d-flex align-items-center gap-2 mb-1">
            <i className="bi bi-images text-primary fs-3"></i>
            <h3 className="fw-bold mb-0">Administración de Imágenes de Artículos</h3>
          </div>
          <p className="text-muted mb-0 small">
            Visualizá, agregá, reordená o eliminá fotografías de los artículos para la empresa activa:{' '}
            <strong className="text-dark">{activeCompanyName}</strong>
          </p>
        </div>

        {/* Tarjetas resumen de cobertura */}
        <div className="d-flex flex-wrap gap-2">
          <button
            type="button"
            className={`btn btn-sm px-3 py-2 rounded-3 border d-flex align-items-center gap-2 ${
              imageFilter === 'all' ? 'btn-dark fw-bold shadow-sm' : 'btn-light text-secondary'
            }`}
            onClick={() => {
              setImageFilter('all');
              setCurrentPage(1);
            }}
          >
            <i className="bi bi-box-seam"></i>
            <span>Todos: {stats.total_articulos.toLocaleString('es-AR')}</span>
          </button>

          <button
            type="button"
            className={`btn btn-sm px-3 py-2 rounded-3 border d-flex align-items-center gap-2 ${
              imageFilter === 'with' ? 'btn-success fw-bold shadow-sm' : 'btn-light text-success border-success-subtle'
            }`}
            onClick={() => {
              setImageFilter('with');
              setCurrentPage(1);
            }}
          >
            <i className="bi bi-camera-fill"></i>
            <span>Con Fotos: {stats.con_imagen.toLocaleString('es-AR')}</span>
          </button>

          <button
            type="button"
            className={`btn btn-sm px-3 py-2 rounded-3 border d-flex align-items-center gap-2 ${
              imageFilter === 'without' ? 'btn-warning fw-bold shadow-sm text-dark' : 'btn-light text-warning-emphasis border-warning-subtle'
            }`}
            onClick={() => {
              setImageFilter('without');
              setCurrentPage(1);
            }}
          >
            <i className="bi bi-image-alt"></i>
            <span>Sin Fotos: {stats.sin_imagen.toLocaleString('es-AR')}</span>
          </button>
        </div>
      </div>

      {/* Barra de Filtros y Buscador */}
      <div className="card shadow-sm border-0 mb-4">
        <div className="card-body p-3">
          <div className="row g-2 align-items-center">
            <div className="col-md-2">
              <label className="form-label small fw-bold text-secondary mb-1">Modo de búsqueda</label>
              <select
                className="form-select form-select-sm"
                value={searchMode}
                onChange={(e) => {
                  setSearchMode(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="general">General (Cód. / Desc.)</option>
                <option value="codigo">Por Código Exacto/Parcial</option>
                <option value="descripcion">Por Descripción</option>
                <option value="filtro_descripc">Palabras en Descripción</option>
                <option value="cod_barras">Código de Barras</option>
              </select>
            </div>

            <div className="col-md-4">
              <label className="form-label small fw-bold text-secondary mb-1">Buscar artículo</label>
              <div className="input-group input-group-sm">
                <span className="input-group-text bg-white">
                  <i className="bi bi-search text-muted"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Escribí código o descripción del artículo..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setSearchTerm('')}
                    title="Limpiar búsqueda"
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                )}
              </div>
            </div>

            <div className="col-md-3">
              <label className="form-label small fw-bold text-secondary mb-1">Rubro</label>
              <select
                className="form-select form-select-sm"
                value={selectedRubro}
                onChange={(e) => {
                  setSelectedRubro(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">Todos los rubros</option>
                {rubros.map((r) => (
                  <option key={r.c_rubro} value={String(r.c_rubro).trim()}>
                    {String(r.c_rubro).trim()} - {r.d_rubro}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label small fw-bold text-secondary mb-1">Estado de Imágenes</label>
              <select
                className="form-select form-select-sm"
                value={imageFilter}
                onChange={(e) => {
                  setImageFilter(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="all">Todos los artículos</option>
                <option value="with">Solo artículos CON imágenes</option>
                <option value="without">Solo artículos SIN imágenes</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Tabla de Artículos e Imágenes */}
      <div className="card shadow-sm border-0">
        <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
          <span className="fw-bold text-secondary small">
            Mostrando {articles.length} de {totalCount.toLocaleString('es-AR')} artículos
          </span>
          <div className="d-flex align-items-center gap-2">
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={currentPage <= 1 || loading}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            >
              <i className="bi bi-chevron-left"></i> Anterior
            </Button>
            <span className="small fw-semibold px-2">
              Página {currentPage} de {totalPages}
            </span>
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={currentPage >= totalPages || loading}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            >
              Siguiente <i className="bi bi-chevron-right"></i>
            </Button>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: '130px' }}>Código</th>
                <th>Descripción del Artículo</th>
                <th className="text-center" style={{ width: '210px' }}>Imágenes Cargadas</th>
                <th className="text-end" style={{ width: '230px' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="4" className="text-center py-5">
                    <Spinner animation="border" variant="primary" size="sm" className="me-2" />
                    <span className="text-muted">Cargando artículos de {activeCompanyName}...</span>
                  </td>
                </tr>
              ) : articles.length === 0 ? (
                <tr>
                  <td colSpan="4" className="text-center py-5 text-muted">
                    <i className="bi bi-inbox fs-2 d-block mb-2"></i>
                    No se encontraron artículos con los filtros seleccionados.
                  </td>
                </tr>
              ) : (
                articles.map((art) => {
                  const slides = getArticleImageSlides(art);
                  const hasImgs = slides.length > 0;
                  return (
                    <tr key={art.c_artic}>
                      <td>
                        <Badge bg="dark" className="font-monospace fs-6 px-2 py-1">
                          {String(art.c_artic).trim()}
                        </Badge>
                      </td>
                      <td>
                        <div className="fw-bold text-dark">{art.d_artic}</div>
                        {art.d_arti2 && <div className="small text-muted">{art.d_arti2}</div>}
                        {(art.f_updat || art.h_updat) && (
                          <div className="text-secondary small mt-1" style={{ fontSize: '0.75rem' }}>
                            <i className="bi bi-clock-history me-1"></i>
                            Modif: {art.f_updat || ''} {art.h_updat ? String(art.h_updat).trim() : ''}
                          </div>
                        )}
                      </td>
                      <td className="text-center">
                        {hasImgs ? (
                          <button
                            type="button"
                            className="btn btn-sm btn-success-subtle text-success border border-success-subtle rounded-pill px-3 py-1 fw-bold d-inline-flex align-items-center gap-2 shadow-sm"
                            onClick={() => handleOpenPreviewModal(art)}
                            title="Hacé clic para ver el detalle de imágenes"
                          >
                            <i className="bi bi-images"></i>
                            <span>
                              {slides.length} {slides.length === 1 ? 'imagen' : 'imágenes'}
                            </span>
                          </button>
                        ) : (
                          <span className="badge bg-light text-secondary border rounded-pill px-3 py-1 fw-normal">
                            <i className="bi bi-image me-1"></i> 0 imágenes
                          </span>
                        )}
                      </td>
                      <td className="text-end">
                        <div className="d-flex justify-content-end gap-2">
                          {hasImgs && (
                            <Button
                              variant="outline-info"
                              size="sm"
                              onClick={() => handleOpenPreviewModal(art)}
                              title="Ver galería / carrusel"
                            >
                              <i className="bi bi-eye"></i>
                            </Button>
                          )}
                          <Button
                            variant={hasImgs ? 'primary' : 'outline-primary'}
                            size="sm"
                            onClick={() => handleOpenManageModal(art)}
                          >
                            <i className="bi bi-cloud-arrow-up me-1"></i>
                            {hasImgs ? 'Gestionar Fotos' : 'Subir Fotos'}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* MODAL DE GESTIÓN DE IMÁGENES DEL ARTÍCULO */}
      <Modal
        show={showManageModal}
        onHide={() => !uploading && setShowManageModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton={!uploading} className="bg-dark text-white">
          <Modal.Title className="fs-6 d-flex align-items-center gap-2">
            <i className="bi bi-images text-info"></i>
            <span>Gestionar Imágenes del Artículo</span>
            {selectedArticle && (
              <Badge bg="primary" className="font-monospace ms-1">
                {String(selectedArticle.c_artic).trim()}
              </Badge>
            )}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="bg-light p-4">
          {selectedArticle && (
            <>
              {/* Datos del artículo */}
              <div className="bg-white p-3 rounded-3 border shadow-sm mb-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div>
                  <div className="fw-bold text-primary fs-6">{selectedArticle.d_artic}</div>
                  {selectedArticle.d_arti2 && (
                    <div className="small text-muted">{selectedArticle.d_arti2}</div>
                  )}
                  <div className="small text-secondary mt-1 d-flex flex-wrap align-items-center gap-3">
                    <span>Empresa: <strong>{activeCompanyName}</strong></span>
                    {(selectedArticle.f_updat || selectedArticle.h_updat) && (
                      <span className="badge bg-light text-secondary border">
                        <i className="bi bi-clock-history me-1 text-primary"></i>
                        Última modif.: {selectedArticle.f_updat || ''} {selectedArticle.h_updat ? String(selectedArticle.h_updat).trim() : ''}
                      </span>
                    )}
                  </div>
                </div>
                {selectedSlides.length > 0 && (
                  <Button
                    variant="outline-danger"
                    size="sm"
                    disabled={uploading}
                    onClick={handleClearAllImages}
                  >
                    <i className="bi bi-trash3 me-1"></i> Vaciar todas ({selectedSlides.length})
                  </Button>
                )}
              </div>

              {/* Zona de Carga (Drag & Drop + Selector de modo Append/Replace) */}
              <div className="bg-white p-3 rounded-3 border shadow-sm mb-3">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                  <span className="fw-bold small text-uppercase text-secondary">
                    <i className="bi bi-cloud-upload me-1 text-primary"></i> Cargar nuevas imágenes
                  </span>
                  <div className="d-flex align-items-center gap-3 small">
                    <div className="form-check form-check-inline mb-0">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="uploadModeRadio"
                        id="modeAppend"
                        checked={uploadMode === 'append'}
                        onChange={() => setUploadMode('append')}
                      />
                      <label className="form-check-label fw-semibold text-success" htmlFor="modeAppend">
                        Agregar a las existentes (Recomendado)
                      </label>
                    </div>
                    <div className="form-check form-check-inline mb-0">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="uploadModeRadio"
                        id="modeReplace"
                        checked={uploadMode === 'replace'}
                        onChange={() => setUploadMode('replace')}
                      />
                      <label className="form-check-label text-danger" htmlFor="modeReplace">
                        Reemplazar todas las anteriores
                      </label>
                    </div>
                  </div>
                </div>

                <div
                  className={`border border-2 rounded-3 p-4 text-center transition-all ${
                    dragActive ? 'border-primary bg-primary-subtle' : 'border-dashed bg-light'
                  }`}
                  style={{ borderStyle: 'dashed', cursor: uploading ? 'not-allowed' : 'pointer' }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!uploading) setDragActive(true);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDragActive(false);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDragActive(false);
                    if (!uploading && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                      handleUploadFiles(e.dataTransfer.files);
                    }
                  }}
                  onClick={() => !uploading && fileInputRef.current && fileInputRef.current.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".jpg,.jpeg,.png,.webp,.gif,.svg,.bmp"
                    className="d-none"
                    onChange={(e) => {
                      if (e.target.files && e.target.files.length > 0) {
                        handleUploadFiles(e.target.files);
                      }
                    }}
                  />
                  {uploading ? (
                    <div className="py-2">
                      <Spinner animation="border" variant="primary" className="mb-2" />
                      <div className="fw-bold text-primary">Subiendo y procesando imágenes...</div>
                    </div>
                  ) : (
                    <>
                      <i className="bi bi-cloud-arrow-up-fill text-primary fs-1 d-block mb-1"></i>
                      <div className="fw-bold text-dark">
                        Hacé clic aquí o arrastrá una o varias imágenes
                      </div>
                      <small className="text-muted">
                        Soporta múltiples archivos a la vez (JPG, PNG, WEBP, GIF, SVG)
                      </small>
                    </>
                  )}
                </div>
              </div>

              {/* Galería de imágenes actuales del artículo */}
              <div className="bg-white p-3 rounded-3 border shadow-sm">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span className="fw-bold small text-uppercase text-secondary">
                    <i className="bi bi-collection me-1 text-primary"></i> Imágenes actuales ({selectedSlides.length})
                  </span>
                  {selectedSlides.length > 1 && (
                    <small className="text-muted">
                      La primera imagen (Portada) es la que se muestra por defecto en las tarjetas.
                    </small>
                  )}
                </div>

                {selectedSlides.length === 0 ? (
                  <div className="text-center py-4 text-muted">
                    <i className="bi bi-image fs-1 d-block mb-2 opacity-50"></i>
                    <span>Este artículo todavía no tiene imágenes cargadas.</span>
                  </div>
                ) : (
                  <div className="row g-3">
                    {selectedSlides.map((slide, idx) => (
                      <div key={`${slide.raw}_${idx}`} className="col-sm-6 col-md-4">
                        <div
                          className={`card h-100 shadow-sm ${
                            idx === 0 ? 'border-primary border-2' : 'border'
                          }`}
                        >
                          <div className="position-relative bg-light text-center p-2" style={{ height: '160px' }}>
                            <CandidateThumbnail
                              slide={slide}
                              alt={slide.raw}
                              className="h-100 w-100 rounded"
                              style={{ objectFit: 'contain' }}
                            />
                            {idx === 0 ? (
                              <Badge bg="primary" className="position-absolute top-0 start-0 m-2 shadow-sm">
                                <i className="bi bi-star-fill me-1"></i> Portada (1°)
                              </Badge>
                            ) : (
                              <Badge bg="dark" className="position-absolute top-0 start-0 m-2 opacity-75">
                                #{idx + 1}
                              </Badge>
                            )}
                          </div>
                          <div className="card-body p-2 d-flex flex-column justify-content-between">
                            <div
                              className="small font-monospace text-truncate fw-semibold mb-2"
                              title={slide.raw}
                            >
                              {slide.raw}
                            </div>
                            <div className="d-flex justify-content-between gap-1">
                              {idx > 0 ? (
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  className="w-100 py-1"
                                  style={{ fontSize: '0.75rem' }}
                                  disabled={uploading}
                                  onClick={() => handleSetPrimaryImage(slide.raw)}
                                  title="Usar esta foto como portada principal"
                                >
                                  <i className="bi bi-star me-1"></i> Hacer Portada
                                </Button>
                              ) : (
                                <span className="small text-primary fw-semibold d-flex align-items-center">
                                  <i className="bi bi-check-circle-fill me-1"></i> Principal
                                </span>
                              )}
                              <Button
                                variant="outline-danger"
                                size="sm"
                                className="py-1 px-2"
                                disabled={uploading}
                                onClick={() => handleDeleteSingleImage(slide.raw)}
                                title="Eliminar esta imagen"
                              >
                                <i className="bi bi-trash"></i>
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </Modal.Body>

        <Modal.Footer className="bg-light">
          <Button
            variant="secondary"
            size="sm"
            disabled={uploading}
            onClick={() => setShowManageModal(false)}
          >
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* MODAL DE VISTA PREVIA / CARRUSEL */}
      <ArticleImageModal
        show={showPreviewModal}
        onHide={() => setShowPreviewModal(false)}
        article={previewArticle}
      />
    </div>
  );
};

export default ArticleImagesAdmin;
